// src/pages/PendingActions/PendingActions.js
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Modal, Form } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { 
  ClipboardList, 
  RefreshCw, 
  Clock, 
  FileX, 
  AlertTriangle,
  Info
} from 'lucide-react';
import StatsCard from './components/StatsCard';
import PatientTable from './components/PatientTable';
import UploadModal from './components/UploadModal';
import DNAModal from './components/DNAModal';
import FilterSection from './components/FilterSection';
import { getGLPUserList, uploadConsultationReport, markPatientAsDNA, dischargePatient } from '../../Services/Service';
import 'react-toastify/dist/ReactToastify.css';
import './PendingActions.scss';

const PendingActions = () => {
  // State Management
  const [pendingActions, setPendingActions] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
   const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [dischargeReason, setDischargeReason] = useState('');
  const [isDischarging, setIsDischarging] = useState(false);
  
  // Modal States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDNAModal, setShowDNAModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  const handleSave = async (patientId, localState) => {
      // 1. Find the patient object to check flags
      const patient = pendingActions.find(p => p.id === patientId);
      if (!patient) return;

      const isFirstAppt = patient.first_appointment_date;
      const cycleId = patient.cycleId;

      if (!cycleId) {
          toast.error("Cycle ID missing for this patient.");
          return;
      }

      setLoading(true);
      try {
          const formData = new FormData();
          formData.append('cycle_id', cycleId);
          formData.append('patient_id', patientId);

          // --- SCENARIO 1: FIRST APPOINTMENT (Date Save Only) ---
          if (isFirstAppt) {
              // Ensure we have a date
              if (!patient.nextConsultationDate) {
                  toast.error("Please select a date.");
                  setLoading(false);
                  return;
              }
              formData.append('next_consultation_date', patient.nextConsultationDate);
              // We do NOT append file or medicines here, triggering the backend's "Scenario 1"
          } 
          // --- SCENARIO 2: REGULAR UPLOAD (File + Meds) ---
          else {
              if (!localState.selectedFile) {
                  toast.error("Please select a report file before saving.");
                  setLoading(false);
                  return;
              }
              
              formData.append('file', localState.selectedFile);
              formData.append('medicines', JSON.stringify(localState.medicines || []));
              formData.append('notes', localState.notes || '');
              
              // Only send date if it exists (usually pre-filled by backend for next cycle)
              if (patient.nextConsultationDate) {
                  formData.append('next_consultation_date', patient.nextConsultationDate);
              }
          }

          // 4. Call API service
          const response = await uploadConsultationReport(patientId, formData);

          if (response && response.data && response.data.status === 'success') {
              toast.success(response.data.message || 'Updated successfully!');
              loadPendingActions(); // Refresh data to see status change
          } else {
              toast.error(response?.data?.message || 'Failed to update record');
          }
      } catch (error) {
          toast.error('An error occurred while saving.');
          console.error('Save error:', error);
      } finally {
          setLoading(false);
      }
  };

  const [filters, setFilters] = useState({
    search: '',
    reportStatus: '',
    patientType: ''
  });

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Stats
  const [stats, setStats] = useState({
    totalPending: 0,
    reportsMissing: 0,
    newPatients: 0,
    overdue: 0
  });

  // Load Data
  useEffect(() => {
    loadPendingActions();
  }, [currentPage, filters]);

  // Calculate Stats
  useEffect(() => {
    calculateStats();
  }, [pendingActions]);

  // Apply Filters
  useEffect(() => {
    applyFilters();
  }, [pendingActions, filters]);

  const loadPendingActions = async () => {
    setLoading(true);
    try {
      const response = await getGLPUserList(
        currentPage,
        itemsPerPage,
        '',
        '',
        filters.search,
        filters.reportStatus,
        filters.patientType
      );

      if (response && response.data && response.data.status === 'success') {
        const transformedData = transformApiData(response.data.data || []);
        setPendingActions(transformedData);
        setLastUpdated(new Date());
      } else {
        toast.error(response?.data?.message || 'Failed to fetch data');
        setPendingActions([]);
      }
    } catch (error) {
      toast.error('Failed to load pending actions');
      console.error('Error loading pending actions:', error);
      setPendingActions([]);
    } finally {
      setLoading(false);
    }
  };

  // Transform API response to table format
  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) {
      console.warn('API data is not an array:', apiData);
      return [];
    }

    return apiData.map((item, index) => {
      const cycleNumber = item.cycleNumber || item.cycle_number || 1;
      const lastConsultDate = item.lastConsultationDate || item.last_consultation_date || null;
      
      const isFirstAppointment = item.is_first_appointment === 1 || item.is_first_appointment === true;
      const firstConsultDate = item.first_consultation_date || '';
      
      let dateToDisplay = item.nextConsultationDate || item.next_consultation_date || '';
      
      if (isFirstAppointment && firstConsultDate && firstConsultDate !== '0000-00-00') {
          dateToDisplay = firstConsultDate;
      } else if(isFirstAppointment){
        dateToDisplay = '';
      }

      return {
        id: item.user_id || item.id || index + 1,
        odooPatientId: item.odooPatientId || null,
        cycleId: item.cycleId || item.cycle_id || null,
        patientName: item.fullName || item.patient_name || item.patientName || 'Unknown',
        nhsId: item.nhsNumber || item.nhs_id || item.nhsId || 'N/A',
        lastConsultationDate: lastConsultDate,
        
        nextConsultationDate: dateToDisplay,
        
        // Critical flags for UI logic
        first_appointment_date: isFirstAppointment, 
        first_consultation_date: firstConsultDate,

        reportStatus: item.reportStatus || item.report_status || 'pending',
        cycleNumber: parseInt(cycleNumber) || 1,
        isNewPatient: isFirstAppointment, // Use the flag directly for stats
        isOverdue: item.is_overdue === 1 || item.is_overdue === true,
        missedMonth: item.missedMonth || item.missed_month || null,
        registrationDate: item.registrationDate || item.registration_date || null
      };
    });
  };

  const calculateStats = () => {
    const totalPending = pendingActions.length;
    const reportsMissing = pendingActions.filter(p => p.reportStatus === 'missing').length;
    const newPatients = pendingActions.filter(p => p.isNewPatient).length;
    const overdue = pendingActions.filter(p => p.isOverdue).length;

    setStats({ totalPending, reportsMissing, newPatients, overdue });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...pendingActions];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(patient =>
        patient.patientName.toLowerCase().includes(searchLower) ||
        patient.nhsId.toLowerCase().includes(searchLower)
      );
    }

    if (filters.reportStatus) {
      filtered = filtered.filter(patient => patient.reportStatus === filters.reportStatus);
    }

    if (filters.patientType) {
      switch (filters.patientType) {
        case 'new':
          filtered = filtered.filter(patient => patient.isNewPatient);
          break;
        case 'existing':
          filtered = filtered.filter(patient => !patient.isNewPatient);
          break;
        case 'overdue':
          filtered = filtered.filter(patient => patient.isOverdue);
          break;
        default:
          break;
      }
    }

    setFilteredActions(filtered);
    // Note: If you want filter changes to reset page to 1, uncomment below
    // setCurrentPage(1); 
  }, [pendingActions, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ search: '', reportStatus: '', patientType: '' });
    setCurrentPage(1);
  };

  // Upload/DNA Modal Handlers (Legacy props, can be kept if needed for modal triggers)
  const handleOpenUploadModal = (patient) => {
    setSelectedPatient(patient);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleOpenDNAModal = (patient) => {
    setSelectedPatient(patient);
    setShowDNAModal(true);
  };

  const handleCloseDNAModal = () => {
    setShowDNAModal(false);
    setSelectedPatient(null);
  };

  const handleConfirmDNA = async (patientId, cycleId, reason, notes) => {
    if (!cycleId || !reason) {
      toast.error('Missing required info for DNA.');
      return;
    }

    try {
      const response = await markPatientAsDNA(cycleId, reason, '', notes || '');
      if (response && response.data && response.data.status === 'success') {
        toast.success('Patient marked as DNA.');
        loadPendingActions(); // Refresh list
        handleCloseDNAModal();
      } else {
        throw new Error(response?.data?.message || 'Failed.');
      }
    } catch (error) {
      toast.error(error.message || 'Error marking DNA.');
    }
  };

  // Date Update Handler - Updates local state immediately so input works
  const handleDateUpdate = (patientId, newDate) => {
    setPendingActions(prev => prev.map(p => 
      p.id === patientId ? { ...p, nextConsultationDate: newDate } : p
    ));
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredActions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredActions.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const formatLastUpdated = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  const handleOpenDischarge = (patient) => {
    setSelectedPatient(patient);
    setDischargeReason('');
    setShowDischargeModal(true);
  }; 
  
  const handleConfirmDischarge = async () => {
      if (!selectedPatient) return;
      
      setIsDischarging(true);
      try {
          const response = await dischargePatient(selectedPatient.cycleId,selectedPatient.id, dischargeReason);
          
          if (response.data && response.data.status === 'success') {
              toast.success('Patient discharged successfully');
              setShowDischargeModal(false);
              loadPendingActions();
          } else {
              toast.error(response.data.message || 'Failed to discharge');
          }
      } catch (error) {
          toast.error('Error processing discharge');
          console.error(error);
      } finally {
          setIsDischarging(false);
      }
  };

  return (
    <div className="pending-actions-page">
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />

      <Container fluid className="py-4">
       <Row className="mb-4">
          <Col>
            <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div className="page-left flex-grow-1 min-w-0">
                <h1 className="page-title d-flex align-items-center gap-2">
                  <ClipboardList className="text-primary" size={32} />
                  Pending Actions
                </h1>
                <p className="page-description text-muted mb-0 responsive-desc">
                  Manage monthly GLP consultation requirements.
                </p>
              </div>
              <div className="last-updated-badge responsive-badge">
                <RefreshCw size={14} className="text-success me-2" />
                <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} lg={4}>
            <StatsCard icon={<Clock size={24} />} iconColor="pink" value={stats.totalPending} label="Total Pending" />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <StatsCard icon={<FileX size={24} />} iconColor="pink" value={stats.reportsMissing} label="Reports Missing" />
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <StatsCard icon={<AlertTriangle size={24} />} iconColor="pink" value={stats.overdue} label="Overdue" />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <FilterSection filters={filters} onFilterChange={handleFilterChange} onReset={handleResetFilters} />
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="table-card">
              <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">Patients Requiring Action</h5>
                <Button variant="primary" size="sm" onClick={loadPendingActions} disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" className="me-1" /> : <RefreshCw size={16} className="me-1" />}
                  Refresh
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : (
                  <PatientTable
                    patients={currentItems}
                    onDNA={handleOpenDNAModal}
                    onDateUpdate={handleDateUpdate}
                    onSave={handleSave}
                    onDischarge={handleOpenDischarge}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* DNA Modal */}
      <DNAModal
        show={showDNAModal}
        onHide={handleCloseDNAModal}
        patient={selectedPatient}
        onConfirm={handleConfirmDNA}
      />
      <Modal show={showDischargeModal} onHide={() => setShowDischargeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Discharge Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to discharge <strong>{selectedPatient?.patientName}</strong>?</p>
          <p className="text-muted small">This will close the current cycle and mark the patient as discharged.</p>
          
          <Form.Group className="mt-3">
            <Form.Label>Reason for Discharge (Optional)</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              value={dischargeReason}
              onChange={(e) => setDischargeReason(e.target.value)}
              placeholder="e.g., Patient request, Treatment completed..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDischargeModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleConfirmDischarge} disabled={isDischarging}>
            {isDischarging ? <Spinner size="sm" animation="border" /> : 'Confirm Discharge'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PendingActions;